import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, Link, Tooltip, Typography } from '@mui/material'
import { PhysicianSelect } from '../components/Elements/Inputs/PhysicianSelect'
import { useProviderSearch } from 'api/useProviderSearch'
import { useSearchParams, useNavigate } from 'react-router-dom'

import { useProviderLookup } from 'api/useProviderLookup'
import useDebounce from 'utils/useDebounce'
import { PageWrapper } from '../components/Modules/Wrappers/PageWrapper'
import {
  PhysicianResult,
  PaymentItem,
  PhysicianSearchGrid,
} from 'components/Modules/DataDisplay/PhysicianSearchGrid'
import { TextBox } from 'components/Elements/Typography/TextBox'
import { ConfirmPaymentDialog } from 'components/Elements/Dialogs/ConfirmPaymentDialog'
import { Close, Email } from '@mui/icons-material'
import { NumberInput } from 'components/Elements/Inputs/NumberInput'
import { Toast } from 'components/Elements/Feedback/Toast'
import { usePhysicianEmailStatement } from '../api/usePhysicianEmailStatement'
import { parseEmailStatementError } from '../utils/emailErrorParser'

export interface ProviderSearhProps { }

export interface PaymentData {
  paymentItems: Array<PaymentItem>
  total: string
}

const calculateTotal = (values: Array<PaymentItem>) => {
  return values
    .reduce((partialSum, a) => {
      if (typeof a.amount === 'string' && a.amount === '') {
        return partialSum
      } else if (typeof a.amount === 'string') {
        return partialSum + parseFloat(a.amount.replace(/[^0-9.-]+/g, ''))
      } else {
        return partialSum + a.amount
      }
    }, 0)
    .toFixed(2)
}

export const ProviderSearch: React.FC<ProviderSearhProps> = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const { providers: provider } = useProviderLookup({
    id: +(searchParams.get('physicianId') ?? '0'),
  })

  const [rows, setRows] = React.useState<
    Array<PaymentItem & { isNew?: boolean }>
  >([])
  const [ids, setIds] = useState<Array<string>>([])
  const [open, setOpen] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [providerDismissed, setProviderDismissed] = useState(false)
  const [toastOpen, setToastOpen] = React.useState(false)
  const [toastMessage, setToastMessage] = React.useState('')
  const [toastSeverity, setToastSeverity] = React.useState<'error' | 'warning' | 'success'>('error')

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedValue = useDebounce<string>(searchTerm, 500)
  const { results, loading } = useProviderSearch({ searchTerm: debouncedValue })

  const { requestEmailStatementAsync, isLoading: isEmailLoading } = usePhysicianEmailStatement({
    onSuccess: () => {
      setToastMessage('Email statement sent successfully!')
      setToastSeverity('success')
      setToastOpen(true)
    },
    onError: (error) => {
      const parsedError = parseEmailStatementError(error)
      setToastMessage(parsedError.message)
      setToastSeverity(parsedError.severity)
      setToastOpen(true)
    }
  })

  const handleEmailClick = async (physicianId: string) => {
    console.log('Email statement requested for physician:', physicianId)
    try {
      await requestEmailStatementAsync(physicianId)
    } catch (error) {
      console.error('Failed to send email statement:', error)
    }
  }

  const handleToastClose = () => {
    setToastOpen(false)
  }

  const onPayNow = () => {
    setShowConfirmation(true)
  }

  const handleClick = (item: PhysicianResult) => {
    if (rows.length < 30) {
      setRows((oldRows) => [
        ...oldRows,
        {
          ...item,
          balance:
            item.statusId === 6 && item.legalCanPay ? 'legal' : item.balance,
          amount: item.statusId === 6 && item.legalCanPay ? '0' : item.balance,
        },
      ])
    }
    else {
      alert("You can only select up to 30 providers at a time.")
    }
  }

  const handleDeleteClick = (id: string) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id))
    if (provider && id === provider.id) {
      setProviderDismissed(true)
    }
  }

  useEffect(() => {
    setIds(rows.map((x) => x.id))
  }, [rows])

  useEffect(() => {
    if (provider && !providerDismissed && !rows.find((x) => x.id === provider.id)) {
      setRows((prevRows) => [...prevRows, provider])
    }
  }, [provider, providerDismissed, rows])

  return (
    <PageWrapper>
      <ConfirmPaymentDialog
        open={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        handleConfirm={(token) => {
          const data = rows.map((item) => ({
            ...item,
            amount: parseFloat(item.amount.toString()).toFixed(2),
          }))
          navigate('/payment', { state: { details: data, token } })
        }}
        items={rows.map((item) => ({
          ...item,
          amount: parseFloat(item.amount.toString()).toFixed(2),
        }))}
      />
      <Typography mb={1} variant="h3" component="h1">
        Provider Payment Portal - NICA's Pay Online Feature
      </Typography>
      <Typography mb={3}>
        Pay your fee by credit card here online. It's free, quick, easy, and secure. A provided phone number will be used by NICA if there are questions regarding payment.
      </Typography>
      <Typography mb={3}>
        NICA's electronic billing process is designed to enhance your annual payment experience, reduce waste, eliminate unwanted mail, ensure the security of your payment, and avoid the additional fee for using a credit card.
      </Typography>
      <TextBox sx={{ marginBottom: 3 }}>
        <Typography fontWeight="bold" mb={1} sx={{ textDecoration: 'underline' }}>
          PLEASE READ - IMPORTANT NOTICES:
        </Typography>
        <Typography fontWeight="bold" mb={1}>
          - Payments made through NICA's Pay Online feature will post as “PURCHASE FLORIDA BIRT TALLAHASSEE FL” on your credit card statement.
        </Typography>
        <Typography fontWeight="bold" mb={1}>
          - <span style={{ textDecoration: 'underline' }}>DO NOT PAY</span> if you are exempt. If you are unsure if you qualify for an exemption, please visit: <Link href="https://nica.com/medical-providers/exemptions/#documents-required" target='_BLANK' color="#00548B">Exemptions</Link>.
        </Typography>
        <Typography fontWeight="bold" mb={1}>
          - If you have been instructed to deduct{' '}
          <Typography component="span" display="inline" fontWeight="bold" sx={{ textDecoration: 'underline' }}>
            INTEREST
          </Typography>{' '}
          from your payment, the amount can only be edited on this screen.
          If you fail to deduct the proper interest amount, NICA{' '}
          <Typography component="span" display="inline" fontWeight="bold" sx={{ textDecoration: 'underline' }}>
            CANNOT
          </Typography>{' '}
          refund it.
        </Typography>
        <Typography fontWeight="bold" mb={1}>
          - If the provider HAS NOT paid in full, because payments take up to two (2) business days to post, the provider will still remain visible in the search, and duplicate payments CAN be made.
        </Typography>
        <Typography fontWeight="bold">
          - The limit for the number of physicians that can be selected for a single payment is 30.
        </Typography>
        <Typography fontWeight="bold">
          - You can now request a copy of your statement by email using the email icon next to the “Amount to Pay” column in the table below.
        </Typography>
      </TextBox>
      <Typography mb={3}>
        To pay balance due for a physician, please search Physician Name or License Number and select the physician(s). Use your invoice email to verify your name, address, license, and invoice amount. Please validate the License Number is correct prior to proceeding to the payment screen.
      </Typography>
      <PhysicianSelect
        open={open}
        setOpen={setOpen}
        handleClick={handleClick}
        selectedIds={[...ids]}
        results={results}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loading={loading}
        sx={{
          marginBottom: 3,
        }}
      />
      <Box display={{ xs: 'block', md: 'none' }}>
        {rows.map((row) => (
          <Card key={row.id} sx={{ boxShadow: 3, marginBottom: 2 }}>
            <CardHeader
              title={`${row.firstName} ${row.lastName}`}
              subheader={`${row.license}`}
              action={
                <>
                  <Tooltip title="Email me a copy of my statement" arrow>
                    <span>
                      <IconButton 
                        disabled={isEmailLoading}
                        onClick={() => handleEmailClick(row.id)} 
                        aria-label={`Email statement for ${row.firstName} ${row.lastName}`}
                      >
                        <Email />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton onClick={() => handleDeleteClick(row.id)} aria-label={`Remove ${row.firstName} ${row.lastName}`}>
                    <Close />
                  </IconButton>
                </>
              }
              sx={{ paddingBottom: 1 }}
            />
            <Divider />
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid container item xs={12}>
                  <Grid item xs={6}>
                    <Typography>
                      Balance
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography textAlign='right' pr={1.8}>
                      {`$ ${parseFloat(row.balance).toFixed(2)}`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={12}>
                  <Grid item xs={6}>
                    <Typography pt={1}>
                      Amount to Pay
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display='flex' alignItems='flex-start'>
                      <Typography textAlign='right' padding={1}>
                        $
                      </Typography>
                      <NumberInput
                        placeholder={'Enter Amount...'}
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        color="primary"
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={row.amount}
                        InputProps={{
                          inputProps: {
                            style: { textAlign: 'right' },
                            max: parseFloat(row.balance)
                          }
                        }}
                        error={ Number.isNaN(row.amount) || Number(row.amount) <= 0 }
                        helperText={ Number.isNaN(row.amount) || Number(row.amount) <= 0 ? "Amount must be greater than $0" : "" }
                        onChange={(event) => {
                          let newValue = event.target.value;

                          if (Number.isNaN(newValue) || parseFloat(newValue) <= 0) {
                            newValue = '';
                          }

                          setRows((prevRows) =>
                            prevRows.map((r) =>
                              r.id === row.id ? { ...r, amount: newValue } : r
                            )
                          );
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>
      <PhysicianSearchGrid
        sx={{ display: { xs: 'none', md: 'flex' }, minHeight: '230px',height: '35vh', width: '100%' }}
        handleDeleteClick={handleDeleteClick}
        handleMailClick={() => {}} // Placeholder function - can be implemented as needed
        disableMail={false}
        rows={rows}
        setRows={setRows}
      />
      <Box flexDirection='row' display='flex' justifyContent={{ xs: 'space-between', md: 'end' }} alignItems='center' mt={5}>
        <Typography fontSize={{ xs: 22, md: 16 }} mr={4} fontWeight={'bold'}>{`Total: $${calculateTotal(
          rows
        )}`}</Typography>

        <Button
          disabled={ rows.length === 0 || rows.some(row => Number.isNaN(row.amount) || Number(row.amount) <= 0) }
          type="button"
          size="medium"
          variant="contained"
          color="secondary"
          onClick={onPayNow}
        >
          Pay Now
        </Button>
      </Box>
      <Toast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleToastClose}
      />
    </PageWrapper>
  )
}
